import React from "react";
import { useTranslation } from "react-i18next";

/**
 * Customer Search Bar Component
 * @param {Object} props
 * @param {string} props.searchQuery - Current search query
 * @param {Function} props.onSearchQueryChange - Handler for search query changes
 * @param {Function} props.onSearch - Handler for search action
 * @param {Function} props.onClear - Handler for clearing search
 * @param {Function} props.onKeyPress - Handler for key press events
 * @param {boolean} props.isSearchMode - Whether currently in search mode
 * @param {string} props.activeSearchQuery - Active search query
 * @param {number} props.resultsCount - Number of search results
 */
const CustomerSearchBar = ({
  searchQuery,
  onSearchQueryChange,
  onSearch,
  onClear,
  onKeyPress,
  isSearchMode,
  activeSearchQuery,
  resultsCount,
}) => {
  const { t } = useTranslation();

  return (
    <div className="mb-6">
      <div className="flex items-center gap-3 max-w-md">
        <div className="relative flex-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            className="w-full pl-10 pr-8 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder={t("customers.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            onKeyPress={onKeyPress}
          />
          {searchQuery && (
            <button
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              onClick={onClear}
              title={t("customers.clearSearch")}
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
        <button
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          onClick={onSearch}
          disabled={!searchQuery.trim()}
        >
          {t("customers.searchButton")}
        </button>
      </div>

      {/* Search Results Header */}
      {isSearchMode && activeSearchQuery && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg
                className="h-4 w-4 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="text-sm font-medium text-blue-800">
                {resultsCount > 0
                  ? t("customers.searchResultsMessage", {
                      query: activeSearchQuery,
                    })
                  : t("customers.noSearchResults")}
              </span>
            </div>
            <button
              className="px-3 py-1 text-xs font-medium text-blue-600 bg-white border border-blue-200 rounded hover:bg-blue-50 transition-colors"
              onClick={onClear}
            >
              {t("customers.clearSearch")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSearchBar;
